import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import './PortsDropdown.css';

// Rendering the whole master would freeze the field; both the curated open-list
// and the search results are capped at this many options.
const MAX_DROPDOWN_OPTIONS = 50;

const PortsDropdown = ({ 
  value, 
  onChange, 
  className, 
  placeholder = "Please enter country name or port name",
  isHighlighted = false,
  onValidationChange = () => {} // New prop for validation status
}) => {
  const [searchValue, setSearchValue] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredPorts, setFilteredPorts] = useState([]);
  const [ports, setPorts] = useState([]);          // full UN/LOCODE master — used when typing
  const [curatedPorts, setCuratedPorts] = useState([]); // the desk's curated Cargo list — shown on open
  const [error, setError] = useState(false); // Error state for validation
  const inputRef = useRef(null);
  const validSelectionRef = useRef(value || ''); // Reference to track valid selection
  const dropdownRef = useRef(null);
  const isSelectingRef = useRef(false); // Track if user is in the process of selecting
  const hasInteractedRef = useRef(false); // Track if user has interacted with the component

  // Update searchValue when value prop changes
  useEffect(() => {
    setSearchValue(value || '');
    validSelectionRef.current = value || '';
    setError(false);
  }, [value]);

  // Determine class names based on className prop
  const isTradeRecap = className === 'trade-recap-ports-dropdown';
  const containerClass = isTradeRecap ? 'trade-recap-ports-search' : 'hms-ports-search';
  const containerInnerClass = isTradeRecap ? 'trade-recap-ports-search__container' : 'hms-ports-search__container';
  const inputClass = isTradeRecap ? 'trade-recap-ports-search__input' : 'hms-ports-search__input';
  const dropdownClass = isTradeRecap ? 'trade-recap-ports-search__dropdown' : 'hms-ports-search__dropdown';
  const optionClass = isTradeRecap ? 'trade-recap-ports-search__option' : 'hms-ports-search__option';
  const clearBtnClass = isTradeRecap ? 'trade-recap-ports-search__clear-btn' : 'hms-ports-search__clear-btn';
  const errorClass = isTradeRecap ? 'trade-recap-ports-search__error' : 'hms-ports-search__error';

  // Store latest callbacks in refs to avoid stale closures
  const onValidationChangeRef = useRef(onValidationChange);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onValidationChangeRef.current = onValidationChange; }, [onValidationChange]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Add click outside listener to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSelectingRef.current) {
        return;
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
        
        if (hasInteractedRef.current) {
          const currentSearchValue = inputRef.current?.value || '';
          const lastValid = validSelectionRef.current;
          
          // If the current input matches the last valid selection, skip validation
          if (currentSearchValue === lastValid) {
            setError(false);
            onValidationChangeRef.current(true);
            return;
          }
          
          const isValid = validatePortInput(currentSearchValue);
          
          if (!isValid && currentSearchValue) {
            setError(true);
            onValidationChangeRef.current(false);
            
            setSearchValue(lastValid);
            onChangeRef.current(lastValid);
          } else {
            setError(false);
            onValidationChangeRef.current(true);
          }
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch ports on component mount.
  //
  // TWO lists, on purpose. Cargo traders complained they "can't see any of
  // their ports": this field used to render nothing until you typed, over a
  // ~7,500-row master that is mostly inland UN/LOCODEs nobody trades. So we
  // load the CURATED cargo list (the ports the desk actually uses, managed on
  // the Cargo Ports tab) and show it the moment the field is focused, while the
  // full master stays behind the search box so a one-off port is never
  // unreachable. If the curated list is empty — nobody has curated it yet — the
  // open-list falls back to the master, so this can never be worse than before.
  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const [curatedRes, masterRes] = await Promise.all([
          axiosInstance.get('/api/offer/ports/', { params: { cargo_only: 'true' } }),
          axiosInstance.get('/api/offer/ports/'),
        ]);
        setCuratedPorts(Array.isArray(curatedRes.data) ? curatedRes.data : []);
        setPorts(Array.isArray(masterRes.data) ? masterRes.data : []);
      } catch (error) {
        console.error('Error fetching ports:', error);
      }
    };
    fetchPorts();
  }, []);

  // The list shown when the field is opened with an empty search box.
  const openListPorts = (curatedPorts.length ? curatedPorts : ports).slice(0, MAX_DROPDOWN_OPTIONS);

  // Function to validate if input is a proper port selection
  const validatePortInput = (input) => {
    // Empty input is valid (not required)
    if (!input || input.trim() === '') return true;
    
    // Valid port format: "1sp 1sb [Port], [Country], [UNLOCODE]"
    const isValidFormat = input.startsWith('1sp 1sb ') && 
                          input.split(',').length === 3;
    
    return isValidFormat;
  };
  
  // Central validation function
  const validateInput = () => {
    // Skip validation if in selection process
    if (isSelectingRef.current) return true;
    
    const isValid = validatePortInput(searchValue);
    
    if (!isValid && searchValue) {
      setError(true);
      onValidationChange(false);
      
      // If invalid, revert to last valid selection
      setSearchValue(validSelectionRef.current);
      onChange(validSelectionRef.current);
      return false;
    } else {
      setError(false);
      onValidationChange(true);
      return true;
    }
  };

  // Clear functionality
  const handleClear = (e) => {
    e.stopPropagation(); // Prevent triggering other handlers
    setSearchValue('');
    setError(false);
    setShowDropdown(false);
    setFilteredPorts([]);
    validSelectionRef.current = '';
    onChange('');
    onValidationChange(true);
    hasInteractedRef.current = true; // Mark as interacted
    
    // Keep focus in input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Search handling
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    hasInteractedRef.current = true; // Mark as interacted
    
    if (!value.trim()) {
      // Cleared back to empty — fall back to the curated list rather than
      // closing the dropdown on the user.
      setFilteredPorts(openListPorts);
      setShowDropdown(openListPorts.length > 0);
      setError(false);
      onValidationChange(true);
      return;
    }

    // Typing searches the FULL master (curated list included — it is a subset),
    // so an unusual one-off port is still reachable.
    const searchTerm = value.toLowerCase();
    const searchPool = ports.length ? ports : curatedPorts;
    const filtered = searchPool.filter(port =>
      port.country_name.toLowerCase().includes(searchTerm) ||
      port.port_name.toLowerCase().includes(searchTerm) ||
      port.unlocode.toLowerCase().includes(searchTerm)
    ).slice(0, MAX_DROPDOWN_OPTIONS);

    setFilteredPorts(filtered);
    setShowDropdown(true);
  };

  // Handle input focus
  const handleFocus = () => {
    hasInteractedRef.current = true; // Mark as interacted

    // Empty box: show the curated list straight away instead of a blank field.
    if (!searchValue) {
      if (openListPorts.length > 0) {
        setFilteredPorts(openListPorts);
        setShowDropdown(true);
      }
      return;
    }

    // Show dropdown if there's text and matches
    if (searchValue && ports.length > 0) {
      const searchTerm = searchValue.toLowerCase();
      const filtered = (ports.length ? ports : curatedPorts).filter(port =>
        port.country_name.toLowerCase().includes(searchTerm) ||
        port.port_name.toLowerCase().includes(searchTerm) ||
        port.unlocode.toLowerCase().includes(searchTerm)
      ).slice(0, MAX_DROPDOWN_OPTIONS);
      
      if (filtered.length > 0) {
        setFilteredPorts(filtered);
        setShowDropdown(true);
      }
    }
  };

  // Selection handling
  const handlePortSelect = (port) => {
    // Flag that we're in selection process to prevent validation
    isSelectingRef.current = true;
    hasInteractedRef.current = true; // Mark as interacted
    
    // Create formatted port value
    const formattedValue = `1sp 1sb ${port.port_name}, ${port.country_name}, ${port.unlocode}`;
    
    // Update internal state and ref
    setSearchValue(formattedValue);
    validSelectionRef.current = formattedValue;
    
    // Clear any error states
    setError(false);
    onValidationChange(true);
    
    // Update parent component
    onChange(formattedValue);
    
    // Close dropdown
    setShowDropdown(false);
    
    // Reset selection flag after a longer delay to ensure click outside doesn't interfere
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 300);
  };

  return (
    <div className={containerClass} ref={dropdownRef}>
      <div className={containerInnerClass}>
        <input 
          ref={inputRef}
          type="text"
          className={`${inputClass} ${isHighlighted ? 'highlighted-field' : ''} ${error ? 'error-field' : ''}`}
          placeholder={placeholder}
          value={searchValue}
          onChange={handleSearchChange}
          onFocus={handleFocus}
        />
        <button 
          className={clearBtnClass}
          onClick={handleClear}
          type="button"
        >
          Clear
        </button>
      </div>
      {error && (
        <div className={errorClass}>
          Please select a port from the dropdown
        </div>
      )}
      {showDropdown && filteredPorts.length > 0 && (
        <div className={dropdownClass}>
          {filteredPorts.map((port) => (
            <div 
              key={port.unlocode}
              className={optionClass}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent input blur
                handlePortSelect(port);
              }}
            >
              {`${port.port_name}, ${port.country_name}, ${port.unlocode}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortsDropdown; 